import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { trackCartAction, trackEvent } from '@/utils/tracking';

interface Product {
  id: string;
  name: string;
  price: number;
  image: string;
}

type GrindOption = 'whole' | 'ground';

interface CartItem extends Product {
  quantity: number;
  grindOption: GrindOption;
}

interface CartContextType {
  cartItems: CartItem[];
  addToCart: (product: Product, quantity: number, grindOption: GrindOption) => void;
  updateQuantity: (productId: string, quantity: number, grindOption?: GrindOption) => void;
  removeFromCart: (productId: string, grindOption: GrindOption) => void;
  clearCart: () => void;
  cartCount: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

const OBHA_COFFEE_PRODUCT: Product = {
  id: 'obha-original',
  name: 'قهوة أبهة الأصلية',
  price: 40,
  image: '/product-front.webp',
};

const OBHA_DRIP_BAG_PRODUCT: Product = {
  id: 'obha-drip-bag',
  name: 'أظرف قهوة مختصة',
  price: 49,
  image: '/drip-bag-product.webp',
};

const CART_STORAGE_KEY = 'obha_cart_v1';

function loadCartFromStorage(): CartItem[] {
  if (typeof window === 'undefined') return [];
  try {
    const saved = localStorage.getItem(CART_STORAGE_KEY);
    if (!saved) return [];
    const parsed = JSON.parse(saved);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (item: unknown) =>
        typeof item === 'object' && item !== null &&
        'id' in item && 'quantity' in item && typeof (item as CartItem).quantity === 'number'
    ) as CartItem[];
  } catch (err) {
    console.error('[CartContext]', 'Failed to parse cart from storage', err);
    return [];
  }
}

function saveCartToStorage(items: CartItem[]): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  } catch (err) {
    console.error('[CartContext]', 'Failed to save cart to storage', err);
  }
}

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>(loadCartFromStorage);

  useEffect(() => {
    saveCartToStorage(cartItems);
  }, [cartItems]);

  const addToCart = (product: Product, quantity: number, grindOption: GrindOption) => {
    trackCartAction('add', product.id, quantity);
    setCartItems(prevItems => {
      const existingItem = prevItems.find(item => item.id === product.id && item.grindOption === grindOption);
      
      if (existingItem) {
        const newQuantity = existingItem.quantity + quantity;
        return prevItems.map(item =>
          item.id === product.id && item.grindOption === grindOption ? { ...item, quantity: newQuantity } : item
        );
      } else {
        return [...prevItems, { ...product, quantity, grindOption }];
      }
    });
  };

  const updateQuantity = (productId: string, quantity: number, grindOption?: GrindOption) => {
    setCartItems(prevItems =>
      prevItems.map(item =>
        item.id === productId && (grindOption === undefined || item.grindOption === grindOption)
          ? { ...item, quantity: Math.max(0, quantity) }
          : item
      ).filter(item => item.quantity > 0)
    );
  };

  const removeFromCart = (productId: string, grindOption: GrindOption) => {
    trackCartAction('remove', productId, 1);
    setCartItems(prevItems =>
      prevItems.filter(item => !(item.id === productId && item.grindOption === grindOption))
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const cartCount = cartItems.reduce((count, item) => count + item.quantity, 0);

  const value = useMemo(() => ({
    cartItems, addToCart, updateQuantity, removeFromCart, clearCart, cartCount
  }), [cartItems, cartCount]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export { OBHA_COFFEE_PRODUCT, OBHA_DRIP_BAG_PRODUCT };